import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Sparkles } from "lucide-react";
import Reveal from "@/components/Reveal";

export const cardClass = "bg-[#FBF9F4] border border-[#E7DFD0] rounded-3xl shadow-[0_8px_30px_-14px_rgba(28,24,21,0.16)] hover:shadow-[0_20px_46px_-18px_rgba(225,37,27,0.28)] hover:-translate-y-1 transition-all duration-300";

function CountUp({ value }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(value);

  useEffect(() => {
    const m = String(value).match(/^([^0-9]*)([\d,.]+)(.*)$/);
    if (!m) return;
    const target = parseFloat(m[2].replace(/,/g, ""));
    if (Number.isNaN(target)) return;
    const el = ref.current;
    const io = new IntersectionObserver(([e]) => {
      if (!e.isIntersecting) return;
      io.disconnect();
      const start = performance.now();
      const dur = 1400;
      const tick = (now) => {
        const p = Math.min((now - start) / dur, 1);
        const eased = 1 - Math.pow(1 - p, 3);
        setDisplay(`${m[1]}${Math.round(target * eased).toLocaleString()}${m[3]}`);
        if (p < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }, { threshold: 0.4 });
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [value]);

  return <span ref={ref}>{display}</span>;
}

export function HomeHero({ c }) {
  const marquee = c.marquee || [];
  return (
    <section className="relative overflow-hidden">
      <div className="absolute inset-y-0 left-0 w-2/3 orange-wash opacity-70 pointer-events-none" />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-12 sm:pt-16 pb-16 relative">
        <div className="grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-7 t-hero">
            <Reveal>
              <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-heading)] mb-6">
                {c.hero.kicker}
              </p>
            </Reveal>
            <Reveal delay={0.06}>
              <h1 className="font-display text-[15vw] sm:text-[6.5rem] lg:text-[8.5rem] leading-[1] tracking-tight text-[var(--brand-primary)]">
                <span className="block">{c.hero.line1}</span>
                <span className="block mt-2 sm:mt-3">{c.hero.line2}</span>
              </h1>
            </Reveal>
            <Reveal delay={0.14}>
              <div className="mt-6 flex items-center gap-3">
                <span className="inline-block w-10 h-[3px] bg-[var(--brand-primary)]" />
                <p className="font-display text-xl sm:text-2xl text-[var(--brand-heading)] tracking-tight">
                  {c.hero.tagline}
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <p className="mt-6 text-lg leading-relaxed text-[var(--brand-muted)] max-w-xl font-medium">
                {c.hero.description}
              </p>
            </Reveal>
            <Reveal delay={0.26}>
              <div className="mt-9 flex flex-wrap gap-4">
                <Link
                  to="/blog"
                  data-testid="hero-read-insights-button"
                  className="group bg-[var(--brand-primary)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm flex items-center gap-2 transition-all duration-300 hover:bg-[var(--brand-primary-dark)] shadow-[0_10px_30px_-10px_rgba(225,37,27,0.6)]"
                >
                  {c.hero.ctaPrimary}
                  <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
                <Link
                  to="/about"
                  data-testid="hero-about-button"
                  className="border border-[var(--brand-heading)] text-[var(--brand-heading)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm transition-all duration-300 hover:bg-[var(--brand-heading)] hover:text-[var(--brand-cream)]"
                >
                  {c.hero.ctaSecondary}
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="md:col-span-5">
            <Reveal delay={0.22}>
              <div className="relative">
                <div className="absolute -inset-8 bg-[color-mix(in_srgb,var(--brand-primary)_18%,transparent)] rounded-[3rem] blur-3xl animate-float-slow" />
                <div className="absolute -top-5 -left-5 w-24 h-24 bg-[#F7A23B]/50 rounded-full blur-xl animate-float-slow" style={{ animationDelay: "1.5s" }} />
                <img
                  src={c.hero.photo}
                  alt={`${c.hero.line1} ${c.hero.line2}`}
                  fetchPriority="high"
                  decoding="async"
                  className="relative w-full h-[520px] object-cover object-top rounded-[2.25rem] shadow-[0_30px_60px_-20px_rgba(28,24,21,0.45)]"
                />
              </div>
            </Reveal>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="overflow-hidden py-4 border-y border-[#E7DFD0]">
        <div className="flex w-max whitespace-nowrap animate-marquee">
          {[...marquee, ...marquee].map((m, i) => (
            <span key={`${m}-${i}`} className="font-display text-lg sm:text-2xl text-[var(--brand-heading)] mx-4 sm:mx-6 flex items-center gap-4 sm:gap-6">
              {m} <span className="text-[var(--brand-primary)]">✦</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HomeStats({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-16 sm:py-20">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
        {(c.stats || []).map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <div className={`${cardClass} p-8`}>
              <p className="font-display text-6xl text-[var(--brand-primary)] leading-none"><CountUp value={s.value} /></p>
              <p className="mt-3 text-xs font-bold uppercase tracking-[0.12em] text-[var(--brand-heading)]">{s.label}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function HomeServices({ c }) {
  return (
    <section className="bg-[var(--brand-surface)]">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-4 t-heading">{c.services.kicker}</p>
          <h2 className="font-display text-5xl sm:text-7xl tracking-tight max-w-3xl leading-[0.96] whitespace-pre-line t-heading">
            {c.services.heading}
          </h2>
        </Reveal>
        <div className="mt-16 grid md:grid-cols-3 gap-6">
          {(c.services.items || []).map((item, i) => (
            <Reveal key={item.t} delay={i * 0.1}>
              <div className={`${cardClass} p-8 h-full`}>
                <span className="font-display text-3xl text-[var(--brand-primary)]">{item.n}</span>
                <h3 className="font-display text-2xl text-[var(--brand-heading)] mt-4 tracking-tight">{item.t}</h3>
                <p className="mt-4 text-[var(--brand-muted)] leading-relaxed font-medium">{item.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}

export function HomeLatest({ c, posts }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 py-20 sm:py-28">
      <div className="flex items-end justify-between mb-14">
        <Reveal>
          <p className="font-mono-brand text-xs uppercase tracking-[0.25em] text-[var(--brand-primary)] mb-4 t-heading">{c.latest.kicker}</p>
          <h2 className="font-display text-5xl sm:text-7xl tracking-tight t-heading">{c.latest.heading}</h2>
        </Reveal>
        <Link to="/blog" data-testid="home-view-all-link" className="hidden sm:flex items-center gap-2 text-sm font-bold uppercase tracking-[0.12em] text-[var(--brand-heading)] hover:text-[var(--brand-primary)] transition-colors">
          View all <ArrowUpRight size={18} />
        </Link>
      </div>
      <div className="grid md:grid-cols-3 gap-6">
        {posts.map((p, i) => (
          <Reveal key={p._id} delay={i * 0.1}>
            <Link to={`/blog/${p.slug}`} data-testid={`home-post-card-${i}`} className={`group block overflow-hidden ${cardClass}`}>
              <div className="overflow-hidden">
                <img src={p.cover_image} alt={p.title} loading="lazy" decoding="async" className="w-full h-52 object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6">
                <p className="font-mono-brand text-xs uppercase tracking-[0.18em] text-[var(--brand-primary)]">{p.category}</p>
                <h3 className="font-display text-2xl mt-3 leading-tight tracking-tight text-[var(--brand-heading)] group-hover:text-[var(--brand-primary)] transition-colors">{p.title}</h3>
                <p className="mt-3 text-[var(--brand-muted)] leading-relaxed line-clamp-2 font-medium">{p.excerpt}</p>
              </div>
            </Link>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function HomeAITeaser({ c }) {
  return (
    <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
      <Reveal>
        <div className="bg-[var(--brand-primary)] text-[var(--brand-cream)] rounded-[2rem] p-10 sm:p-16 flex flex-col md:flex-row items-start md:items-center gap-8 justify-between shadow-[0_30px_60px_-24px_rgba(225,37,27,0.6)]">
          <div className="flex items-start gap-5">
            <div className="bg-[color-mix(in_srgb,var(--brand-cream)_15%,transparent)] text-[var(--brand-cream)] p-3 rounded-2xl"><Sparkles size={24} /></div>
            <div>
              <h3 className="font-display text-3xl sm:text-4xl tracking-tight">{c.aiTeaser.heading}</h3>
              <p className="mt-3 text-[color-mix(in_srgb,var(--brand-cream)_85%,transparent)] max-w-xl font-medium">{c.aiTeaser.description}</p>
            </div>
          </div>
          <Link to="/blog" className="bg-[var(--brand-heading)] text-[var(--brand-cream)] px-7 py-4 rounded-full font-bold uppercase tracking-[0.1em] text-sm whitespace-nowrap transition-all hover:bg-black">
            {c.aiTeaser.cta}
          </Link>
        </div>
      </Reveal>
    </section>
  );
}
